<template>

    <div>

        <Head :title="__('Dashboard')" />

        <div v-if="counter_scope !== 'student'" class="flex flex-wrap items-center gap-2 p-3 mb-5 bg-white rounded-lg shadow-lg">
            <span class="mr-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{{ __('Period') }}</span>
            <button v-for="option in period.options" :key="option.key" type="button"
                class="px-3 py-1.5 text-xs font-medium border rounded-full transition-colors"
                :class="(customOpen ? option.key === 'custom' : option.key === period.key)
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'"
                @click="setPeriod(option.key)">
                {{ __(option.label) }}
            </button>
            <form v-if="customOpen || period.key === 'custom'" class="flex flex-wrap items-center gap-2" @submit.prevent="applyCustomRange">
                <input v-model="customFrom" type="date" :max="customTo || today" class="px-2 py-1 text-xs border border-gray-300 rounded" required />
                <span class="text-xs text-gray-500">{{ __('to') }}</span>
                <input v-model="customTo" type="date" :min="customFrom" :max="today" class="px-2 py-1 text-xs border border-gray-300 rounded" required />
                <button type="submit" class="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded">{{ __('Apply') }}</button>
            </form>
            <span class="w-full text-xs text-gray-500 sm:w-auto sm:ml-auto">{{ period.from }} → {{ period.to }}</span>
        </div>

        <div v-for="group in counterGroups" :key="group.title" class="mb-5">
            <h2 class="flex items-center gap-2 mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                {{ __(group.title) }}
                <span v-if="counter_scope !== 'student'" class="px-1.5 py-0.5 normal-case rounded"
                    :class="group.live ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'">
                    {{ group.live ? __('Live') : __(period.label) }}
                </span>
            </h2>
            <div class="grid w-full grid-cols-1 gap-custom sm:grid-cols-2 xl:grid-cols-4">
                <a v-for="card in group.cards" :key="card.key" :href="card.link"
                    class="block col-span-1 badge__item" :class="{ 'pointer-events-none': !card.link }">
                    <div class="flex w-full h-full p-4 bg-white border-l-4 rounded-lg shadow-lg custom-panel"
                        :class="card.alert ? 'border-yellow-500' : 'border-transparent'">
                        <div class="flex-1 min-w-0">
                            <span class="block mb-1 text-sm font-semibold text-gray-600">{{ __(card.label) }}</span>
                            <div class="flex items-baseline gap-2">
                                <span class="text-2xl font-bold text-gray-900">{{ card.value }}</span>
                                <span v-if="card.trend" class="px-1.5 py-0.5 text-xs font-semibold rounded"
                                    :class="card.trend.good ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                                    {{ card.trend.text }}
                                </span>
                            </div>
                            <span v-for="(hint, i) in card.hints" :key="i"
                                class="block mt-1 text-xs text-gray-500 truncate">{{ hint }}</span>
                        </div>
                        <div class="pl-2">
                            <div class="flex items-center justify-center w-8 h-8 text-white rounded-full" :class="card.color">
                                <i class="fa" :class="card.icon" aria-hidden="true"></i>
                            </div>
                        </div>
                    </div>
                </a>
            </div>
        </div>

        <div v-if="counter_scope === 'desk'" class="mb-5">
            <div class="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h2 class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    {{ __('Staff monitoring (all queues)') }}
                    <span class="px-1.5 py-0.5 ml-1 normal-case text-indigo-700 bg-indigo-100 rounded">{{ __('Caseload live · outcomes :period', { period: __(period.label) }) }}</span>
                </h2>
                <label class="flex items-center gap-2 text-xs text-gray-600">
                    <input v-model="staffWithWorkOnly" type="checkbox" />
                    {{ __('Only staff with open tickets or activity in this period') }}
                </label>
            </div>
            <div class="overflow-x-auto bg-white rounded-lg shadow-lg">
                <table class="w-full text-sm whitespace-nowrap">
                    <thead>
                        <tr class="text-xs text-left text-gray-500 border-b">
                            <th v-for="col in staffColumns" :key="col.key" class="px-4 py-3 font-semibold cursor-pointer select-none"
                                :class="{ 'text-right': col.numeric }" :title="col.title ? __(col.title) : null"
                                @click="sortStaff(col.key)">
                                {{ __(col.label) }}<span v-if="staffSort.key === col.key">{{ staffSort.desc ? ' ▼' : ' ▲' }}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="m in staffRows" :key="m.id" class="border-b last:border-b-0 hover:bg-gray-50">
                            <td class="px-4 py-2">
                                <a :href="m.link" class="font-semibold text-gray-900 hover:underline">{{ m.name }}</a>
                                <span v-if="m.inactive" class="ml-1 px-1.5 py-0.5 text-xs font-semibold text-red-700 bg-red-100 rounded">{{ __('Inactive') }}</span>
                                <span class="block text-xs text-gray-500">{{ m.role }}</span>
                            </td>
                            <td class="px-4 py-2 font-semibold text-right">{{ m.open }}</td>
                            <td class="px-4 py-2 text-right">{{ m.pending }}</td>
                            <td class="px-4 py-2 text-right">{{ m.processing }}</td>
                            <td class="px-4 py-2 text-right">{{ m.online_open }}</td>
                            <td class="px-4 py-2 text-right" :class="{ 'text-yellow-700 font-semibold': m.expiring > 0 }">{{ m.expiring }}</td>
                            <td class="px-4 py-2 text-right" :class="{ 'text-red-700 font-semibold': m.oldest_open_hours >= 48 }">
                                {{ m.oldest_open_hours !== null ? formatHours(m.oldest_open_hours) : '—' }}
                            </td>
                            <td class="px-4 py-2 text-right">{{ m.completed }}</td>
                            <td class="px-4 py-2 text-right" :class="{ 'text-red-700 font-semibold': m.expired > 0 }">{{ m.expired }}</td>
                            <td class="px-4 py-2 text-right">{{ formatSeconds(m.first_reply_seconds) }}</td>
                            <td class="px-4 py-2 text-right">{{ formatSeconds(m.resolution_seconds) }}</td>
                            <td class="px-4 py-2 text-right" :class="{ 'text-red-700 font-semibold': m.rating !== null && m.rating < 3 }">
                                {{ m.rating !== null ? m.rating + ' (' + m.ratings + ')' : '—' }}
                            </td>
                        </tr>
                        <tr v-if="!staffRows.length">
                            <td :colspan="staffColumns.length" class="px-4 py-6 text-center text-gray-500">{{ __('No staff activity to show') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Student Graph -->
        <div v-if="auth.user.role.slug == 'student'"
            class="grid grid-cols-1 gap-5 mt-5 response__details sm:grid-cols-1 md:grid-cols-12">
            <div class="col-span-12 md:col-span-6">
                <div class="shadow-sm custom_card custom-card">
                    <div class="custom-card-header ">
                        <h5 class="mb-0 fw-bold">{{ __('Ticket by Status') }} ({{ count_tickets_by_status }}) </h5>
                    </div>
                    <div class="card-body">
                        <div class="pt-3">
                            <MyNewChartComponent :department-data="top_statuses"
                                style="height: 270px !important; width: 430px !important; height: 315px !important; user-select: none;" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-span-12 md:col-span-6">
                <div class="shadow-sm custom_card custom-card">
                    <div class="custom-card-header ">
                        <h5 class="mb-0 fw-bold">{{ __('Department Ticket Assigned') }} </h5>
                    </div>
                    <div class="card-body">
                        <div class="pt-3">
                            <ReportBarChart :series-data="bar_chart_dept_series" :categories="bar_chart_dept_categories"
                                style="height: 270px !important;    width: 430px !important; height: 315px !important; user-select: none;" />
                        </div>
                    </div>
                </div>
            </div>

        </div>
        <!-- Admin Graph -->


        <div v-if="auth.user.role.slug !== 'student'"
            class="grid grid-cols-1 gap-5 mt-5 response__details sm:grid-cols-1 md:grid-cols-12">

            <div class="col-span-12 md:col-span-6">
                <div class="shadow-sm custom_card custom-card">
                    <div class="custom-card-header ">
                        <h5 class="mb-0 fw-bold">
                            {{ __('Ticket by Departments') }} ({{ count_tickets_by_department }})
                        </h5>
                    </div>
                    <div class="card-body">
                        <div class="pt-3">
                            <MyNewChartComponent :department-data="top_departments"
                                :style="{ height: '315px', width: '100%', userSelect: 'none' }" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-span-12 md:col-span-6">
                <div class="shadow-sm custom_card custom-card">
                    <div class="custom-card-header ">
                        <h5 class="mb-0 fw-bold">{{ __('Department Ticket Assigned') }} </h5>
                    </div>
                    <div class="card-body">
                        <div class="pt-3">
                            <ReportBarChart :series-data="bar_chart_dept_series" :categories="bar_chart_dept_categories"
                                style="height: 270px !important;    width: 430px !important; height: 315px !important; user-select: none;" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="auth.user.role.slug !== 'student'"
            class="grid grid-cols-1 gap-5 mt-5 response__details sm:grid-cols-1 md:grid-cols-12">

            <div class="col-span-12 md:col-span-6">
                <div class="shadow-sm custom_card custom-card">
                    <div class="custom-card-header ">
                        <h5 class="mb-0 fw-bold">{{ __('Ticket by Status') }} ({{ count_tickets_by_status }}) </h5>
                    </div>
                    <div class="card-body">
                        <div class="pt-3">
                            <MyNewChartComponent :department-data="top_statuses"
                                style="height: 270px !important; width: 430px !important; height: 315px !important; user-select: none;" />
                        </div>
                    </div>
                </div>
            </div>



            <div class="col-span-12 md:col-span-6">
                <div class="shadow-sm custom_card custom-card">
                    <div class="custom-card-header ">
                        <h5 class="mb-0 fw-bold">{{ __('Status Ticket Assigned') }} </h5>
                    </div>
                    <div class="card-body">
                        <div class="pt-3">
                            <ReportBarChart :series-data="bar_chart_status_series"
                                :categories="bar_chart_status_categories"
                                style="height: 270px !important;    width: 430px !important; height: 315px !important; user-select: none;" />
                        </div>
                    </div>
                </div>
            </div>


        </div>

        <div class="grid grid-cols-1 gap-5 mt-5 response__details sm:grid-cols-1 md:grid-cols-12"
            v-if="auth.user.role.slug !== 'student'">

            <div class="w-full col-span-12 md:col-span-12" style=" margin-bottom: 20px;">
                <div class="flex flex-col p-5 bg-white rounded-lg shadow-lg r__wrapper rd">
                    <h2 class="pt-3 text-lg font-bold th__ttl">{{ __('Ticket history') }}</h2>
                    <div class="flex th__info">
                        <span class="text-xl font-bold"> {{ chart_line.this_month }} </span>
                        <span class="pt-2 pl-1 pr-1 text-xs">/ {{ __('this month') }}</span>
                        <span class="pt-2 pl-2 pr-2 text-xs font-bold">{{ __('last month') }} {{
                            chart_line.last_month }}</span>
                    </div>
                    <div v-if="months.length" class="flex justify-center w-full c__wrapper ">
                        <div class="flex flex-col items-center w-full gap-3 c__line" v-for="cl in months">
                            <span class="cl__b" :style="{ '--cl_value': cl.value }"></span>
                            <span class="text-xs cl__n">{{ cl.month }}</span>
                        </div>
                    </div>
                </div>
            </div>







        </div>


        <!-- Loading Process -->
        <div v-if="loading" class="processing-overlay">
            <div class="background"></div>
            <div class="loader">
                <svg width="200px" height="200px" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"
                    style="background: none;">
                    <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.875s"></animate>
                    </circle>
                    <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.75s"></animate>
                    </circle>
                    <circle cx="50" cy="75" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.625s"></animate>
                    </circle>
                    <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.5s"></animate>
                    </circle>
                    <circle cx="25" cy="50" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.375s"></animate>
                    </circle>
                    <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.25s"></animate>
                    </circle>
                    <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.125s"></animate>
                    </circle>
                    <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="0s"></animate>
                    </circle>
                </svg>
            </div>
        </div>
        <!-- Loading Process -->

    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import VueApexCharts from '@/Shared/donut/t_tickets'
import BarChartComponent from '@/Shared/barchart/t_report_tickets'
import MyNewChartComponent from '@/Shared/barchart/mynewchart'
import ReportBarChart from '@/Shared/barchart/reportbarchart'

export default {
    metaInfo: { title: 'Dashboard' },
    components: {
        Head,
        Icon,
        Link,
        VueApexCharts,
        BarChartComponent,
        MyNewChartComponent,
        ReportBarChart,
    },
    layout: Layout,
    props: {
        auth: Object,
        entries: Array,
        chart_line: Object,
        api_key: String,
        counter_scope: String,
        counters: Object,
        period: Object,
        top_creators: Array,
        top_departments: Array,
        top_statuses: Array,
        count_tickets_by_department: Number,
        count_tickets_by_status: Number,
        top_categories: Array,
        top_types: Array,
        department_assignee_bar_chart_data: Object,
        status_assignee_bar_chart_data: Object,
        bar_chart_dept_categories: Array,
        bar_chart_dept_series: Array,
        bar_chart_status_series: Array,
        bar_chart_status_categories: Array,
    },
    data() {
        return {
            errors: [],
            loading: false,
            months: [],
            staffWithWorkOnly: true,
            staffSort: { key: 'open', desc: true },
            staffColumns: [
                { key: 'name', label: 'Staff' },
                { key: 'open', label: 'Open', numeric: true, title: 'Pending + processing, all queues' },
                { key: 'pending', label: 'Pending', numeric: true },
                { key: 'processing', label: 'Processing', numeric: true },
                { key: 'online_open', label: 'Online', numeric: true, title: 'Open tickets in the Online queue' },
                { key: 'expiring', label: 'Expiring 48h', numeric: true },
                { key: 'oldest_open_hours', label: 'Oldest open', numeric: true },
                { key: 'completed', label: 'Completed', numeric: true, title: 'Completed in the selected period' },
                { key: 'expired', label: 'Expired', numeric: true, title: 'Expired in the selected period' },
                { key: 'first_reply_seconds', label: 'First reply', numeric: true, title: 'Median time to their first comment, tickets raised in the selected period' },
                { key: 'resolution_seconds', label: 'Resolution', numeric: true, title: 'Median raised-to-completed, tickets completed in the selected period' },
                { key: 'rating', label: 'Rating', numeric: true, title: 'Average student rating (count) in the selected period' },
            ],
            customOpen: false,
            customFrom: this.period && this.period.key === 'custom' ? this.period.from : null,
            customTo: this.period && this.period.key === 'custom' ? this.period.to : null,
            today: new Date().toISOString().slice(0, 10),
        }
    },
    computed: {
        counterGroups() {
            const c = this.counters
            if (this.counter_scope === 'student') return this.studentGroups(c)
            if (this.counter_scope === 'staff') return this.staffGroups(c)
            return this.deskGroups(c)
        },
        staffRows() {
            const { key, desc } = this.staffSort
            const rows = (this.counters.staff || []).filter(m =>
                !this.staffWithWorkOnly || m.open > 0 || m.completed > 0 || m.expired > 0)
            // Missing values (—) always sort last.
            return [...rows].sort((a, b) => {
                const x = a[key], y = b[key]
                if (x === null || x === undefined) return 1
                if (y === null || y === undefined) return -1
                const cmp = typeof x === 'string' ? x.localeCompare(y) : x - y
                return desc ? -cmp : cmp
            })
        },
    },

    created() {
        this.months = this.chart_line.previousMonths.map(m => {
            return { 'month': m, 'value': this.chart_line.months[m] ? ((this.chart_line.months[m] * 100) / this.chart_line.total) + '%' : '0%' }
        })
    },
    methods: {
        sortStaff(key) {
            this.staffSort = { key, desc: this.staffSort.key === key ? !this.staffSort.desc : key !== 'name' }
        },
        studentGroups(c) {
            return [{
                title: 'My tickets',
                cards: [
                    { key: 'open', label: 'Open', value: c.pending.total + c.in_progress.total, link: c.pending.total ? c.pending.link : c.in_progress.link, icon: 'fa-envelope-open', color: 'bg-primary', hints: [this.__('Waiting on the support team')] },
                    { key: 'pending', label: 'Pending', value: c.pending.total, link: c.pending.link, icon: 'fa-bell', color: 'bg-success', hints: [this.__('Not yet picked up')] },
                    { key: 'processing', label: 'Processing', value: c.in_progress.total, link: c.in_progress.link, icon: 'fa-cog', color: 'bg-cold', hints: [this.__('A staff member is working on it')] },
                    { key: 'completed', label: 'Completed', value: c.completed.total, link: c.completed.link, icon: 'fa-check', color: 'bg-junk', hints: c.expired.total ? [this.__(':count expired', { count: c.expired.total })] : [] },
                ],
            }]
        },
        // "vs <previous>" hint for a period figure, when the period has a comparison.
        previousHint(previous, format = v => v) {
            if (previous === null || previous === undefined || !this.period.compare) return null
            return this.__('vs :value (:compare)', { value: format(previous), compare: this.__(this.period.compare) })
        },
        // Cards shared by the staff and desk views; the counters are already
        // scoped server-side (assigned to me vs whole desk).
        pendingCard(c, label) {
            return {
                key: 'pending', label, value: c.pending.total, link: c.pending.link,
                icon: 'fa-bell', color: 'bg-success',
                alert: c.pending.oldest_hours >= 48,
                hints: [
                    c.pending.oldest_hours !== null ? this.__('Oldest waiting :age', { age: this.formatHours(c.pending.oldest_hours) }) : this.__('Queue is clear'),
                ],
            }
        },
        processingCard(c) {
            return {
                key: 'processing', label: 'Processing', value: c.in_progress.total, link: c.in_progress.link,
                icon: 'fa-cog', color: 'bg-primary',
                alert: c.in_progress.expiring > 0,
                hints: [this.__(':count expiring within 48h', { count: c.in_progress.expiring })],
            }
        },
        completedCard(c) {
            const done = c.completed
            return {
                key: 'completed', label: 'Completed', value: done.count, link: done.link,
                icon: 'fa-check', color: 'bg-junk',
                trend: this.trend(done.count, done.previous, true),
                hints: [this.previousHint(done.previous), this.__('Completed, Completed AA and UA')].filter(Boolean),
            }
        },
        expiredCard(c) {
            const finished = c.completed.count + c.expired.count
            const rate = finished ? Math.round((c.expired.count * 100) / finished) : null
            return {
                key: 'expired', label: 'Expired', value: c.expired.count, link: c.expired.link,
                icon: 'fa-hourglass-end', color: 'bg-danger',
                alert: rate !== null && rate >= 20,
                trend: this.trend(c.expired.count, c.expired.previous, false),
                hints: [
                    rate !== null ? this.__(':pct% of finished tickets expired', { pct: rate }) : this.__('No finished tickets'),
                    this.previousHint(c.expired.previous),
                ].filter(Boolean),
            }
        },
        resolutionCard(c) {
            return {
                key: 'resolution', label: 'Resolution time', value: this.formatSeconds(c.resolution.median_seconds),
                icon: 'fa-clock', color: 'bg-danger',
                hints: [this.__('Median of :count completed', { count: c.resolution.sample })],
            }
        },
        ratingCard(c, label) {
            return {
                key: 'rating', label, value: c.rating.average !== null ? c.rating.average + ' / 5' : '—',
                icon: 'fa-star', color: 'bg-warning',
                alert: c.rating.average !== null && c.rating.average < 3,
                hints: [this.__(':count ratings', { count: c.rating.total })],
            }
        },
        staffGroups(c) {
            const fr = c.first_reply
            const pending = this.pendingCard(c, 'My pending')
            pending.hints.unshift(this.__('Assigned to you, not started'))
            return [
                {
                    title: 'My caseload', live: true,
                    cards: [
                        pending,
                        this.processingCard(c),
                        {
                            key: 'online', label: 'My online tickets', value: c.online.total, link: c.online.link,
                            icon: 'fa-globe', color: 'bg-cold',
                            hints: [this.__('Open in the Online queue')],
                        },
                    ],
                },
                {
                    title: 'My performance',
                    cards: [
                        this.completedCard(c),
                        this.expiredCard(c),
                        {
                            key: 'first_reply', label: 'My first reply', value: this.formatSeconds(fr.median_seconds),
                            icon: 'fa-reply', color: 'bg-primary',
                            alert: fr.replied_pct !== null && fr.replied_pct < 80,
                            trend: this.trend(fr.median_seconds, fr.previous_median_seconds, false),
                            hints: [
                                fr.replied_pct !== null ? this.__('Replied on :pct% of :count tickets', { pct: fr.replied_pct, count: fr.sample }) : this.__('No tickets raised in this period'),
                                this.previousHint(fr.previous_median_seconds, this.formatSeconds),
                            ].filter(Boolean),
                        },
                        this.resolutionCard(c),
                        this.ratingCard(c, 'My rating'),
                    ],
                },
            ]
        },
        deskGroups(c) {
            const fr = c.first_response
            const r = c.raised
            const pending = this.pendingCard(c, 'Pending pickup')
            pending.hints.unshift(this.__(':count unassigned', { count: c.pending.unassigned }))
            return [
                {
                    title: 'Overview (all queues)',
                    cards: [
                        {
                            key: 'raised', label: 'Tickets raised', value: r.count,
                            icon: 'fa-ticket', color: 'bg-primary',
                            hints: [
                                this.__(':campus on campus · :online online', { campus: r.on_campus, online: r.online }),
                                this.__('Avg :count/day', { count: r.per_day }) + (r.previous !== null ? ' · ' + this.previousHint(r.previous) : ''),
                            ],
                        },
                        this.completedCard(c),
                        this.expiredCard(c),
                        {
                            key: 'still_open', label: 'Raised and still open', value: r.still_open,
                            icon: 'fa-envelope-open', color: 'bg-cold',
                            alert: r.never_assigned > 0 && r.still_open > 0,
                            hints: [this.__(':count never assigned', { count: r.never_assigned })],
                        },
                    ],
                },
                {
                    title: 'Work queue', live: true,
                    cards: [
                        pending,
                        this.processingCard(c),
                        {
                            key: 'mine', label: 'Assigned to me', value: c.mine.total, link: c.mine.link,
                            icon: 'fa-user', color: 'bg-cold',
                            hints: [this.__(':pending pending · :processing processing', { pending: c.mine.pending, processing: c.mine.total - c.mine.pending })],
                        },
                        {
                            key: 'online', label: 'Online queue', value: c.online.total, link: c.online.link,
                            icon: 'fa-globe', color: 'bg-warning',
                            hints: [this.__(':count pending pickup', { count: c.online.pending })],
                        },
                    ],
                },
                {
                    title: 'Assignment & coverage (all queues)', live: true, hidden: true,
                    cards: [
                        {
                            key: 'unassigned', label: 'Unassigned open', value: c.coverage.unassigned_open,
                            icon: 'fa-flag', color: 'bg-danger',
                            alert: c.coverage.unassigned_open > 0,
                            hints: [this.__('Open tickets with no owner')],
                        },
                        {
                            key: 'no_response', label: 'No response over 24h', value: c.coverage.no_response_24h,
                            icon: 'fa-hourglass-half', color: 'bg-danger',
                            alert: c.coverage.no_response_24h > 0,
                            hints: [this.__('Open and not yet answered')],
                        },
                        {
                            key: 'urgent', label: 'Urgent open', value: c.coverage.urgent_open,
                            icon: 'fa-exclamation-triangle', color: 'bg-warning',
                            hints: [this.__('Urgent or Very Urgent priority')],
                        },
                        {
                            key: 'workload', label: 'Staff workload', value: c.workload.staff_with_open, link: c.workload.link,
                            icon: 'fa-users', color: 'bg-primary',
                            hints: [
                                this.__('Staff with open tickets · avg :count each', { count: c.workload.average }),
                                c.workload.busiest_name ? this.__('Busiest: :name (:count)', { name: c.workload.busiest_name, count: c.workload.busiest_total }) : this.__('No open assignments'),
                            ],
                        },
                    ],
                },
                {
                    title: 'Performance (all queues)', hidden: true,
                    cards: [
                        {
                            key: 'first_response', label: 'First response', value: this.formatSeconds(fr.median_seconds),
                            icon: 'fa-reply', color: 'bg-primary',
                            alert: fr.within_24h_pct !== null && fr.within_24h_pct < 80,
                            trend: this.trend(fr.median_seconds, fr.previous_median_seconds, false),
                            hints: [
                                fr.within_24h_pct !== null ? this.__(':pct% answered within 24h', { pct: fr.within_24h_pct }) : this.__('No tickets raised in this period'),
                                this.previousHint(fr.previous_median_seconds, this.formatSeconds),
                            ].filter(Boolean),
                        },
                        this.resolutionCard(c),
                        this.ratingCard(c, 'Student rating'),
                        {
                            key: 'department', label: 'Busiest department', value: c.department.total, link: c.department.link,
                            icon: 'fa-sitemap', color: 'bg-junk',
                            hints: [c.department.name ? this.__(':name · :pct% of raised', { name: c.department.name, pct: c.department.share }) : this.__('No tickets raised in this period')],
                        },
                    ],
                },
            ].filter(group => !group.hidden)
        },
        setPeriod(key) {
            if (key === 'custom') {
                this.customOpen = true
                return
            }
            this.customOpen = false
            this.reloadWithPeriod({ period: key })
        },
        applyCustomRange() {
            if (!this.customFrom || !this.customTo) return
            this.reloadWithPeriod({ period: 'custom', from: this.customFrom, to: this.customTo })
        },
        reloadWithPeriod(params) {
            this.$inertia.get(this.route('dashboard'), params, {
                preserveScroll: true,
                onStart: () => { this.loading = true },
                onFinish: () => { this.loading = false },
            })
        },
        formatHours(hours) {
            return hours < 48 ? hours + 'h' : Math.floor(hours / 24) + 'd'
        },
        formatSeconds(seconds) {
            if (seconds === null || seconds === undefined) return '—'
            if (seconds < 3600) return Math.max(1, Math.round(seconds / 60)) + 'm'
            return this.formatHours(Math.round(seconds / 3600))
        },
        // Percentage change badge; higherIsBetter decides its colour.
        trend(current, previous, higherIsBetter) {
            if (current === null || !previous) return null
            const change = Math.round(((current - previous) * 100) / previous)
            if (change === 0) return null
            return { text: (change > 0 ? '▲ ' : '▼ ') + Math.abs(change) + '%', good: (change > 0) === higherIsBetter }
        },
    },
}
</script>
